// چهار حالت اجباری هر صفحه (DoD Frontend): Loading، Empty، Error، Success.
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { errorMessageKey, useTranslation } from '@phoenix/i18n';
import type { ReactNode } from 'react';
import { faDigits } from '../format/digits';

export interface EmptyStateProps {
  /** کلید i18n عنوان (پیش‌فرض common:states.empty_title). */
  titleKey?: string;
  descriptionKey?: string;
  icon?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ titleKey, descriptionKey, icon, action }: EmptyStateProps) {
  const { t } = useTranslation('common');
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 6, textAlign: 'center' }} data-testid="empty-state">
      <Box sx={{ color: 'text.disabled', fontSize: 48, lineHeight: 1 }} aria-hidden>
        {icon ?? <InboxOutlinedIcon fontSize="inherit" />}
      </Box>
      <Typography variant="h3" component="p">
        {t(titleKey ?? 'common:states.empty_title')}
      </Typography>
      {descriptionKey && <Typography color="text.secondary">{t(descriptionKey)}</Typography>}
      {action}
    </Stack>
  );
}

/** شکل حداقلی خطا برای نمایش (ApiError از @phoenix/api-client با همین فیلدها). */
export interface DisplayableError {
  code?: string;
  status?: number;
  traceId?: string | undefined;
}

function asDisplayable(error: unknown): DisplayableError {
  if (error && typeof error === 'object') {
    const value = error as Record<string, unknown>;
    return {
      code: typeof value.code === 'string' ? value.code : undefined,
      status: typeof value.status === 'number' ? value.status : undefined,
      traceId: typeof value.traceId === 'string' ? value.traceId : undefined,
    } as DisplayableError;
  }
  return {};
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** نمایش فشرده درون کارت یا دیالوگ. */
  compact?: boolean;
}

/** نمایش خطا با متن ترجمه‌شده بر پایه `code` (نه title سرور)، خود کد و کد پیگیری (traceId). */
export function ErrorState({ error, onRetry, compact = false }: ErrorStateProps) {
  const { t } = useTranslation('common');
  const { code, status, traceId } = asDisplayable(error);
  const message = t(code === 'PHX-NET-0000' ? 'common:states.network_error' : errorMessageKey(code, status));

  return (
    <Alert
      severity="error"
      icon={<ErrorOutlineIcon />}
      data-testid="error-state"
      data-error-code={code ?? ''}
      sx={compact ? undefined : { my: 2 }}
      action={
        onRetry ? (
          <Button color="inherit" onClick={onRetry}>
            {t('common:actions.retry')}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{message}</AlertTitle>
      {code && (
        <Typography variant="caption" component="div">
          {t('common:states.error_code')}: <bdi dir="ltr">{code}</bdi>
        </Typography>
      )}
      {traceId && (
        <Typography variant="caption" component="div">
          {t('common:states.trace_id')}: <bdi dir="ltr">{traceId}</bdi>
        </Typography>
      )}
    </Alert>
  );
}

export interface LoadingStateProps {
  /** تعداد ردیف اسکلتون (صفحات فهرست)؛ صفر ← Spinner. */
  rows?: number;
}

export function LoadingState({ rows = 0 }: LoadingStateProps) {
  const { t } = useTranslation('common');
  if (rows > 0) {
    return (
      // نقش status تا aria-label مجاز باشد (axe aria-prohibited-attr روی div بی‌نقش — C-AGENT-1055).
      <Stack
        role="status"
        spacing={1}
        aria-busy="true"
        aria-label={t('common:states.loading')}
        data-testid="loading-state"
      >
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} variant="rounded" height={32} />
        ))}
      </Stack>
    );
  }
  return (
    <Stack alignItems="center" sx={{ py: 6 }} data-testid="loading-state">
      <CircularProgress aria-label={t('common:states.loading')} />
    </Stack>
  );
}

export interface QueryStateProps<T> {
  isPending: boolean;
  error: unknown;
  data: T | undefined;
  /** داده خالی؟ (پیش‌فرض: آرایه خالی یا items خالی). */
  isEmpty?: (data: T) => boolean;
  onRetry?: () => void;
  empty?: ReactNode;
  loading?: ReactNode;
  children: (data: T) => ReactNode;
}

function defaultIsEmpty(data: unknown): boolean {
  if (Array.isArray(data)) {
    return data.length === 0;
  }
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: unknown[] }).items.length === 0;
  }
  return false;
}

/** سوئیچ چهار حالت برای نتیجه TanStack Query. */
export function QueryState<T>({
  isPending,
  error,
  data,
  isEmpty,
  onRetry,
  empty,
  loading,
  children,
}: QueryStateProps<T>) {
  if (isPending) {
    return <>{loading ?? <LoadingState rows={3} />}</>;
  }
  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  if (data === undefined || (isEmpty ?? defaultIsEmpty)(data)) {
    return <>{empty ?? <EmptyState />}</>;
  }
  return <>{children(data)}</>;
}

/** عدد کوچک با ارقام فارسی (کمکی نمایش شمارنده). */
export function FaNumber({ value }: { value: number | string }) {
  return <>{faDigits(value)}</>;
}
